import React, { useContext } from "react";
import { sanitizePiiPreview } from "../../helpers/sanitizePii";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { makeStyles } from "@mui/styles";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

const STATUS_COLOR = {
  pending: "#F4C02F",
  open: "#F37021",
  closed: "#8A9096",
};
const STATUS_LABEL = {
  pending: "Aguardando",
  open: "Atendendo",
  closed: "Resolvido",
};

const useStyles = makeStyles((theme) => ({
  card: {
    padding: "10px 12px",
    marginBottom: 8,
    borderRadius: 8,
    cursor: "grab",
    userSelect: "none",
    transition: "box-shadow 0.2s",
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    },
  },
  cardDragging: {
    opacity: 0.5,
  },
  topRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: 13,
  },
  contactInfo: {
    flex: 1,
    minWidth: 0,
  },
  contactName: {
    fontWeight: 600,
    fontSize: 13,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  contactPhone: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  lastMsg: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginBottom: 6,
  },
  bottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 4,
  },
  timeAgo: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  attendant: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
}));

const timeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return `${diff}s atrás`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
  return `${Math.floor(diff / 86400)}d atrás`;
};

const KanbanCard = ({ ticket }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isPrivileged = user?.profile === "admin" || user?.profile === "supervisor";
  // Atendente comum só arrasta tickets que já são dele ou que estão sem dono
  // (livres pra pegar) — mesma regra aplicada no backend (TicketController.update).
  const canDrag =
    isPrivileged || ticket.userId === null || ticket.userId === user?.id;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: ticket.id, disabled: !canDrag });

  const style = { transform: CSS.Transform.toString(transform), transition };
  const statusColor = STATUS_COLOR[ticket.status] || "#8A9096";
  const statusLabel = STATUS_LABEL[ticket.status] || ticket.status;

  return (
    <Paper
      ref={setNodeRef}
      style={{ ...style, cursor: canDrag ? undefined : "default" }}
      {...attributes}
      {...(canDrag ? listeners : {})}
      className={`${classes.card} ${isDragging ? classes.cardDragging : ""}`}
      variant="outlined"
    >
      <div className={classes.topRow}>
        <Avatar
          src={ticket.contact?.profilePicUrl}
          className={classes.avatar}
          style={{ backgroundColor: ticket.queue?.color || "#F37021" }}
        >
          {ticket.contact?.name?.[0]?.toUpperCase()}
        </Avatar>
        <div className={classes.contactInfo}>
          <Typography className={classes.contactName}>
            {ticket.contact?.name || "Desconhecido"}
          </Typography>
          {ticket.contact?.number && (
            <Typography className={classes.contactPhone}>
              {ticket.contact.number}
            </Typography>
          )}
        </div>
        <Tooltip title={statusLabel}>
          <div className={classes.statusDot} style={{ backgroundColor: statusColor }} />
        </Tooltip>
      </div>

      <Typography className={classes.lastMsg}>
        {ticket.lastMessage ? sanitizePiiPreview(ticket.lastMessage) : "Sem mensagens"}
      </Typography>

      <div className={classes.bottomRow}>
        <Typography className={classes.timeAgo}>
          {timeAgo(ticket.updatedAt)}
        </Typography>
        {ticket.user && (
          <Typography className={classes.attendant}>
            {ticket.user.name}
          </Typography>
        )}
      </div>
    </Paper>
  );
};

export default KanbanCard;
